<template>
  <div class="header-wrapper">
    <!-- Top Notification Bar -->
    <div class="top-bar w-full flex items-center justify-between px-8 py-2 text-sm font-medium bg-black/40 backdrop-blur-md text-white fixed top-0 left-0 z-50 h-10">
      <div class="flex items-center space-x-4">
        <a href="https://www.linkedin.com/company/98654211/" target="_blank" rel="noopener" aria-label="LinkedIn">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm15.5 11.268h-3v-5.604c0-1.337-.025-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.601v5.595z"></path></svg>
        </a>
      </div>
      <div class="flex items-center space-x-2">
        <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h2.28a2 2 0 011.7 1.06l.84 1.68a2 2 0 001.7 1.06h3.54a2 2 0 001.7-1.06l.84-1.68A2 2 0 0116.72 3H19a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" /></svg>
        <span class="ml-1">{{ $t('contact.info.phone.value') }}</span>
      </div>
    </div>
    <!-- Main Header -->
    <header :class="['fixed w-full z-40 transition-all duration-300', { 'bg-gradient-to-r from-white via-gray-50 to-white backdrop-blur-md shadow-xl border-b border-gray-100': headerBg }]" style="top: 40px;">
      <!-- Main Navigation Bar -->
      <div class="border-b" :class="{ 'border-gray-200': headerBg, 'border-gray-700/50 backdrop-blur-md': !headerBg }">
        <nav class="container mx-auto px-4 py-4">
          <div class="flex items-center justify-between">
            <!-- Logo -->
            <router-link to="/" class="flex items-center group z-50">
              <img src="../assets/logo_17112025.png" alt="Heidelbell Logo" class="h-8 sm:h-10 md:h-12 w-auto transition-all duration-300 group-hover:scale-105 group-hover:drop-shadow-lg" />
            </router-link>

            <!-- Desktop Navigation -->
            <div class="hidden md:flex items-center space-x-8">
              <router-link 
                v-for="(route, index) in navigationRoutes" 
                :key="index"
                :to="route.path"
                class="nav-link relative group uppercase"
                :class="{ 
                  'text-black': headerBg && currentRoute === route.path,
                  'text-gray-800': headerBg && currentRoute !== route.path,
                  'text-white': !headerBg && currentRoute === route.path,
                  'text-gray-200': !headerBg && currentRoute !== route.path
                }"
              >
                {{ $t(`nav.${route.name}`) }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </router-link>
              
              <!-- Desktop Language Selector -->
              <div class="relative">
                <button 
                  @click="toggleLanguageDropdown" 
                  class="flex items-center space-x-2 focus:outline-none"
                  :class="{ 'text-gray-800': headerBg, 'text-gray-300': !headerBg }"
                >
                  <span class="fi" :class="`fi-${currentLanguage === 'en' ? 'gb' : 'de'}`"></span>
                  <span class="text-sm font-medium">{{ currentLanguage.toUpperCase() }}</span>
                  <svg class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': isLanguageDropdownOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <!-- Desktop Language Dropdown -->
                <div 
                  v-if="isLanguageDropdownOpen" 
                  class="absolute right-0 mt-2 w-40 bg-gray-800 rounded-lg shadow-lg py-1 transform transition-all duration-200"
                >
                  <button 
                    v-for="lang in languages" 
                    :key="lang.code"
                    @click="selectLanguage(lang.code)" 
                    class="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                  >
                    <span :class="`fi fi-${lang.flag}`"></span>
                    <span>{{ lang.name }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Mobile Menu Button -->
            <button 
              @click="toggleMenu" 
              class="md:hidden relative z-50 p-2 rounded-md hover:bg-gray-700 focus:outline-none"
              :class="{ 'text-gray-800': headerBg, 'text-gray-300': !headerBg }"
              :aria-expanded="isMenuOpen"
            >
              <span class="sr-only">{{ isMenuOpen ? 'Close menu' : 'Open menu' }}</span>
              <div class="flex relative w-6 h-6">
                <span 
                  class="absolute w-6 h-0.5 bg-current transform transition-all duration-200"
                  :class="isMenuOpen ? 'rotate-45 top-3' : 'top-1'"
                ></span>
                <span 
                  class="absolute w-6 h-0.5 bg-current top-3 transition-opacity duration-200"
                  :class="{ 'opacity-0': isMenuOpen }"
                ></span>
                <span 
                  class="absolute w-6 h-0.5 bg-current transform transition-all duration-200"
                  :class="isMenuOpen ? '-rotate-45 top-3' : 'top-5'"
                ></span>
              </div>
            </button>
          </div>
        </nav>
      </div>

              <!-- Mobile Menu Panel -->
        <div 
          class="absolute  md:left-22 h-screen inset-0 bg-gray-900 transform transition-all duration-500 ease-in-out md:hidden"
          :class="{ 'translate-x-0': isMenuOpen, 'translate-x-full': !isMenuOpen }"
          style="z-index: 60;"
        >
        <!-- Mobile Menu Header -->
        <div class="flex items-center justify-end pr-6 border-b border-gray-700">
          <button 
            @click="closeMenu" 
            class="p-3 rounded-full hover:bg-gray-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <svg class="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Mobile Menu Content -->
        <div class="flex flex-col h-full">
          <!-- Mobile Navigation -->
          <nav class="flex-1 p-4">
            <div class="space-y-2">
              <router-link 
                v-for="(route, index) in navigationRoutes" 
                :key="index"
                :to="route.path"
                class="mobile-nav-link group flex items-center justify-between p-6 rounded-xl transition-all duration-300 hover:shadow-lg"
                :class="{ 'bg-primary/20 text-primary border-l-4 border-primary shadow-md': currentRoute === route.path, 'text-gray-200 hover:bg-gray-800': currentRoute !== route.path }"
                @click="closeMenu"
              >
                <div class="flex items-center space-x-4 flex-1">
                  <div class="w-3 h-3 rounded-full transition-colors duration-200 flex-shrink-0" :class="{ 'bg-primary': currentRoute === route.path, 'bg-gray-500': currentRoute !== route.path }"></div>
                  <span class="font-semibold text-lg uppercase">{{ $t(`nav.${route.name}`) }}</span>
                </div>
                <svg class="h-6 w-6 transition-all duration-200 group-hover:translate-x-1 flex-shrink-0" :class="{ 'text-primary': currentRoute === route.path, 'text-gray-400': currentRoute !== route.path }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </router-link>
            </div>
              <!-- Mobile Footer -->
            <!-- Language Selector -->
            <div class="pt-12">
              <div class="relative">
                <button 
                  @click="toggleLanguageDropdown" 
                  class="flex items-center justify-between w-full p-4 rounded-xl text-gray-200 bg-gray-700 border border-gray-600 hover:bg-gray-600 focus:outline-none transition-all duration-200 shadow-sm"
                >
                  <div class="flex items-center space-x-3">
                    <span class="fi" :class="`fi-${currentLanguage === 'en' ? 'gb' : 'de'}`"></span>
                    <span class="font-medium text-base">{{ currentLanguage === 'en' ? 'English' : 'Deutsch' }}</span>
                  </div>
                  <svg 
                    class="h-5 w-5 transition-transform duration-200" 
                    :class="{ 'rotate-180': isLanguageDropdownOpen }" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <!-- Language Dropdown -->
                <div 
                  v-if="isLanguageDropdownOpen" 
                  class="mt-2 w-full bg-gray-700 border border-gray-600 rounded-xl overflow-hidden shadow-lg"
                >
                  <button 
                    v-for="lang in languages" 
                    :key="lang.code"
                    @click="selectLanguage(lang.code)" 
                    class="flex items-center space-x-3 w-full px-4 py-4 text-base text-gray-200 hover:bg-gray-600 transition-colors duration-200"
                  >
                    <span :class="`fi fi-${lang.flag}`"></span>
                    <span class="font-medium">{{ lang.name }}</span>
                  </button>
                </div>
            </div>

            <!-- Contact Info -->
            

            <!-- Copyright -->
            <div class="pt-4 border-t border-gray-700">
              <p class="text-xs text-gray-400 text-center">
                {{ $t('footer.copyright') }}
              </p>
            </div>
          </div>
          </nav>

        
        </div>
      </div>
    </header>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { locale, t } = useI18n()
const route = useRoute()
const router = useRouter()
const isMenuOpen = ref(false)
const isLanguageDropdownOpen = ref(false)
const currentLanguage = ref(locale.value)

// Header background logic
const headerBg = ref(false)

onMounted(() => {
  const handleScroll = () => {
    const scrollY = window.scrollY
    // Check for hero element first, then fallback to viewport height
    const heroElement = document.querySelector('.hero-section')
    const heroHeight = heroElement ? heroElement.offsetHeight : window.innerHeight * 0.7
    const newHeaderBg = scrollY > heroHeight - 44
    headerBg.value = newHeaderBg
  }
  
  window.addEventListener('scroll', handleScroll)
  
  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
  })
})

// Use computed property for current route
const currentRoute = computed(() => route.path)

const navigationRoutes = [
  { path: '/', name: 'home' },
  { path: '/services', name: 'services' },
  { path: '/about', name: 'about' },
  //{ path: '/gallery', name: 'gallery' },
  { path: '/contact', name: 'contact' }
]

const languages = [
  { code: 'en', name: 'English', flag: 'gb' },
  { code: 'de', name: 'Deutsch', flag: 'de' }
]

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  if (isMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeMenu = () => {
  isMenuOpen.value = false
  document.body.style.overflow = ''
  isLanguageDropdownOpen.value = false
}

const toggleLanguageDropdown = () => {
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value
}

const selectLanguage = (lang) => {
  locale.value = lang
  currentLanguage.value = lang
  isLanguageDropdownOpen.value = false
  localStorage.setItem('locale', lang)
}

// Close menu on route change
const routeChangeHandler = () => {
  closeMenu()
}
</script>

<style scoped>
@import 'flag-icons/css/flag-icons.min.css';

.nav-link {
  @apply px-3 py-2 rounded-md font-medium transition-all duration-300 relative overflow-hidden uppercase;
}

.nav-link:not(.text-black):not(.text-gray-800) {
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s;
}

.nav-link:hover::before {
  left: 100%;
}

.nav-link:hover {
  transform: translateY(-1px);
}

.nav-link.text-black,
.nav-link.text-white {
  animation: subtle-pulse 2s ease-in-out infinite;
}

@keyframes subtle-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.mobile-nav-link {
  @apply flex px-4 py-3 text-base font-medium text-gray-200 hover:bg-gray-800/50 rounded-lg uppercase;
}

.fi {
  @apply w-5 h-4 rounded-sm;
}

/* Add a subtle shadow to the header */
header {
  @apply shadow-lg;
}
</style> 